/* Region deep-dive modal.
   Opens when a panel publishes a code through `useRegion`. Fetches
   `/api/dashboard/region/<code>` once per open and renders a richer
   context card — lanes, incidents, fleet presence, top ports, and
   market implications. */

import { useEffect, useState } from "react"
import { api } from "./api"
import { severityClasses } from "./Panel"
import { useRegion } from "./store"

interface RegionLane {
  id: string
  label: string
  score: number
  trend: "up" | "flat" | "down"
}

interface RegionIncident {
  id: string
  title: string
  severity: "low" | "medium" | "high" | "critical"
  summary: string
  started_at: string
}

interface RegionPort {
  name: string
  locode: string
  status: "nominal" | "elevated" | "congested" | "critical"
}

interface RegionMarket {
  indicator: string
  delta_pct: number
  note: string
}

interface RegionFleet {
  total: number
  air: number
  sea: number
  road: number
}

interface RegionDetail {
  code: string
  name: string
  score: number
  score_label: "NOMINAL" | "GUARDED" | "ELEVATED" | "CRITICAL"
  lanes: RegionLane[]
  incidents: RegionIncident[]
  fleet: RegionFleet
  market_implications: RegionMarket[]
  top_ports: RegionPort[]
  as_of: number
}

function scoreColor(label: string): string {
  if (label === "CRITICAL") return "text-red-400"
  if (label === "ELEVATED") return "text-orange-400"
  if (label === "GUARDED") return "text-amber-400"
  return "text-emerald-400"
}

function scoreBar(score: number): string {
  if (score >= 75) return "bg-red-500"
  if (score >= 55) return "bg-orange-500"
  if (score >= 30) return "bg-amber-500"
  return "bg-emerald-500"
}

function portDot(status: string): string {
  if (status === "critical") return "bg-red-500"
  if (status === "congested") return "bg-orange-500"
  if (status === "elevated") return "bg-amber-500"
  return "bg-emerald-500"
}

function trendGlyph(trend: string): string {
  if (trend === "up") return "↑"
  if (trend === "down") return "↓"
  return "→"
}

function trendColor(trend: string): string {
  if (trend === "up") return "text-red-400"
  if (trend === "down") return "text-emerald-400"
  return "text-[color:var(--text-muted)]"
}

function relTime(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime()
  const m = Math.floor(diff / 60_000)
  if (m < 1) return "just now"
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  return `${d}d ago`
}

function relFromEpoch(sec: number): string {
  const diff = Date.now() - sec * 1000
  const s = Math.max(0, Math.floor(diff / 1000))
  if (s < 60) return `${s}s ago`
  const m = Math.floor(s / 60)
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  return `${h}h ago`
}

export function RegionDeepDive() {
  const activeRegion = useRegion((s) => s.activeRegion)
  const closedRegion = useRegion((s) => s.closedRegion)
  const [data, setData] = useState<RegionDetail | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!activeRegion) return
    let cancelled = false
    setLoading(true)
    setError(null)
    setData(null)
    api
      .get<RegionDetail>(`/api/dashboard/region/${activeRegion}`)
      .then((d) => {
        if (!cancelled) setData(d)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          const msg = e instanceof Error ? e.message : String(e)
          setError(msg.includes("404") ? "Unknown region" : "Failed to load region")
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [activeRegion])

  useEffect(() => {
    if (!activeRegion) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closedRegion()
    }
    window.addEventListener("keydown", onKey)
    return () => window.removeEventListener("keydown", onKey)
  }, [activeRegion, closedRegion])

  if (!activeRegion) return null

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Region deep-dive"
      onClick={closedRegion}
    >
      <div
        className="ui-panel ui-panel--glass w-full max-w-[900px] max-h-[85vh] overflow-y-auto relative rounded"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          aria-label="Close"
          onClick={closedRegion}
          className="absolute top-2 right-3 text-[color:var(--text-muted)] hover:text-[color:var(--text)] text-xl leading-none px-2 py-1 rounded focus:outline-none focus:ring-1 focus:ring-[color:var(--accent)]"
        >
          ×
        </button>

        <header className="flex items-center gap-2 px-4 py-2.5 border-b border-[color:var(--border)]">
          <h3 className="ui-header-title truncate">Region deep-dive</h3>
          <div className="flex-1" />
          {data && (
            <span className="text-[10px] text-[color:var(--text-dim)] uppercase tracking-wider">
              as of {relFromEpoch(data.as_of)}
            </span>
          )}
        </header>

        {loading && (
          <div className="px-6 py-16 text-center text-xs uppercase tracking-widest text-[color:var(--text-dim)]">
            loading…
          </div>
        )}

        {error && !loading && (
          <div className="px-6 py-16 text-center text-sm text-red-400">
            {error}
          </div>
        )}

        {data && !loading && !error && <RegionBody data={data} code={activeRegion} />}
      </div>
    </div>
  )
}

function RegionBody({ data, code }: { data: RegionDetail; code: string }) {
  return (
    <div>
      {/* Header */}
      <div className="px-5 pt-5 pb-4 border-b border-[color:var(--border)] flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--text-dim)]">
            {code}
          </div>
          <div className="text-2xl text-[color:var(--text)] mt-1 leading-tight">
            {data.name}
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className={`text-4xl font-bold tabular-nums ${scoreColor(data.score_label)}`}>
              {data.score}
            </div>
            <div className={`text-[10px] uppercase tracking-[0.2em] ${scoreColor(data.score_label)}`}>
              {data.score_label}
            </div>
          </div>
        </div>
      </div>

      {/* Lanes */}
      <Section title="Lanes" count={data.lanes.length}>
        {data.lanes.length === 0 ? (
          <EmptyLine>No lanes tagged for this region.</EmptyLine>
        ) : (
          <div>
            {data.lanes.map((l) => (
              <div key={l.id} className="px-3 py-2 border-b border-[color:var(--border)] last:border-b-0">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className={`w-2 h-2 rounded-full ${scoreBar(l.score)}`} />
                    <span className="text-sm text-[color:var(--text)] truncate">{l.label}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm tabular-nums font-semibold">{l.score}</span>
                    <span className={`text-sm ${trendColor(l.trend)}`}>{trendGlyph(l.trend)}</span>
                  </div>
                </div>
                <div className="mt-1 h-1 w-full bg-[color:var(--border)] rounded overflow-hidden">
                  <div className={`h-1 ${scoreBar(l.score)}`} style={{ width: `${l.score}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>

      {/* Incidents */}
      <Section title="Recent incidents" count={data.incidents.length}>
        {data.incidents.length === 0 ? (
          <EmptyLine>No open incidents mention this region.</EmptyLine>
        ) : (
          <div>
            {data.incidents.map((it) => {
              const sv = severityClasses(it.severity)
              return (
                <div key={it.id} className="px-3 py-2.5 border-b border-[color:var(--border)] last:border-b-0">
                  <div className="flex items-start justify-between gap-2">
                    <span className={`text-[9px] px-1.5 py-0.5 rounded uppercase tracking-wider ${sv.bg} ${sv.text}`}>
                      {it.severity}
                    </span>
                    <span className="text-[9px] text-[color:var(--text-dim)] uppercase">
                      {relTime(it.started_at)}
                    </span>
                  </div>
                  <div className="mt-1 text-sm text-[color:var(--text)] leading-snug">{it.title}</div>
                  {it.summary && (
                    <div className="mt-0.5 text-[11px] text-[color:var(--text-muted)] leading-snug">
                      {it.summary}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </Section>

      {/* Fleet presence */}
      <Section title="Fleet presence">
        <div className="px-3 py-3 grid grid-cols-4 gap-3 text-center">
          <FleetTile label="TOTAL" value={data.fleet.total} icon="■" />
          <FleetTile label="AIR" value={data.fleet.air} icon="✈" />
          <FleetTile label="SEA" value={data.fleet.sea} icon="⛴" />
          <FleetTile label="ROAD" value={data.fleet.road} icon="🚚" />
        </div>
      </Section>

      {/* Top ports */}
      <Section title="Top ports" count={data.top_ports.length}>
        <div>
          {data.top_ports.map((p) => (
            <div
              key={p.locode}
              className="px-3 py-2 border-b border-[color:var(--border)] last:border-b-0 flex items-center gap-3"
            >
              <span className={`w-2 h-2 rounded-full ${portDot(p.status)}`} />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-[color:var(--text)] truncate">{p.name}</div>
                <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-dim)]">
                  {p.locode}
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-wider text-[color:var(--text-muted)]">
                {p.status}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* Market implications */}
      <Section title="Market implications">
        {data.market_implications.length === 0 ? (
          <EmptyLine>No notable market movement tied to this region.</EmptyLine>
        ) : (
          <div>
            {data.market_implications.map((m, i) => {
              const up = m.delta_pct > 0
              const down = m.delta_pct < 0
              const col = up ? "text-red-400" : down ? "text-emerald-400" : "text-[color:var(--text-muted)]"
              const arrow = up ? "↑" : down ? "↓" : "→"
              return (
                <div
                  key={`${m.indicator}-${i}`}
                  className="px-3 py-2 border-b border-[color:var(--border)] last:border-b-0 flex items-center gap-3"
                >
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-dim)] w-20 shrink-0">
                    {m.indicator}
                  </div>
                  <div className={`tabular-nums text-sm w-20 shrink-0 ${col}`}>
                    {arrow} {Math.abs(m.delta_pct).toFixed(1)}%
                  </div>
                  <div className="text-[11px] text-[color:var(--text-muted)] flex-1 leading-snug">
                    {m.note}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </Section>
    </div>
  )
}

function Section({
  title,
  count,
  children,
}: {
  title: string
  count?: number
  children: React.ReactNode
}) {
  return (
    <section className="border-b border-[color:var(--border)] last:border-b-0">
      <div className="flex items-center gap-2 px-4 py-1.5 bg-black/20">
        <h4 className="ui-header-title">{title}</h4>
        {count != null && (
          <span className="text-[10px] text-[color:var(--text-dim)] tabular-nums">
            {count.toLocaleString()}
          </span>
        )}
      </div>
      <div>{children}</div>
    </section>
  )
}

function FleetTile({ label, value, icon }: { label: string; value: number; icon: string }) {
  return (
    <div>
      <div className="text-[9px] uppercase tracking-widest text-[color:var(--text-dim)]">{label}</div>
      <div className="text-xl tabular-nums text-[color:var(--text)] mt-0.5">
        <span className="opacity-60 mr-1">{icon}</span>
        {value}
      </div>
    </div>
  )
}

function EmptyLine({ children }: { children: React.ReactNode }) {
  return (
    <div className="px-4 py-4 text-[11px] text-[color:var(--text-dim)] italic">{children}</div>
  )
}
